import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { FiEye, FiEyeOff, FiLock, FiMail } from "react-icons/fi";
import img from "../../assets/LogoCabecalho.png";
import imgDark from "../../assets/LogoCabecalhoBranca.png";
import Load from "../../components/ui/Load";
import Modal from "../../tailadmin/components/ui/Modal";
import studentsService from "../../services/students";
import { toast } from "react-toastify";
import InputDefault from "../../components/ui/InputDefault";

const LoginPage = () => {
  const { login } = useAuth();
  const [load, setLoad] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Forgot Password State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    setError("");

    if (!validateEmail(email)) {
      setError("Por favor, insira um email válido");
      return;
    }

    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setLoad(true);
    try {
      await login(email, password);
    } catch (err) {
      setError("Email ou senha incorretos. Tente novamente.");
    } finally {
      setLoad(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError("");
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && canSubmit) {
      handleSubmit();
    }
  };

  const handleResetPassword = async () => {
    if (!validateEmail(resetEmail)) {
      toast.error("Por favor, insira um email válido");
      return;
    }

    setResetLoading(true);
    try {
      const success = await studentsService.resetPasswordByEmail(resetEmail);
      if (success) {
        toast.success(
          "Email de recuperação enviado! Verifique sua caixa de entrada.",
        );
        setShowForgotPassword(false);
        setResetEmail("");
      } else {
        toast.error("Erro ao enviar email. Verifique se o email está correto.");
      }
    } catch (error) {
      toast.error("Ocorreu um erro. Tente novamente mais tarde.");
    } finally {
      setResetLoading(false);
    }
  };

  const isEmailValid = email && validateEmail(email);
  const isPasswordValid = password.length >= 6;
  const canSubmit = isEmailValid && isPasswordValid && !load;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-boxdark-2 dark:to-boxdark px-4">
      <div className="w-full max-w-md rounded-2xl border border-stroke bg-white/90 backdrop-blur shadow-xl dark:border-strokedark dark:bg-boxdark transition-all duration-300">
        <div className="px-6 py-10 sm:px-10">
          {/* Logo */}
          <div className="flex flex-col items-center mb-4">
            <img
              src={img}
              alt="Logo"
              className="w-32 mb-4 dark:hidden transition-opacity duration-300"
            />
            <img
              src={imgDark}
              alt="Logo"
              className="w-32 mb-4 hidden dark:block transition-opacity duration-300"
            />
            <h1 className="text-2xl font-semibold text-black dark:text-white">
              Bem-vindo!
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Entre com seu email e senha
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 animate-shake">
              <p className="text-sm text-red-600 dark:text-red-400 text-center">
                {error}
              </p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-5">
            {/* Email */}
            <div>
              <label className="block mb-2 text-sm font-medium text-black dark:text-white">
                Email
              </label>
              <div className="relative">
                <FiMail
                  className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                    emailTouched && !isEmailValid
                      ? "text-red-400"
                      : emailTouched && isEmailValid
                        ? "text-green-500"
                        : "text-gray-400"
                  }`}
                />
                <input
                  type="email"
                  placeholder="seu@email.com"
                  autoComplete="email"
                  className={`w-full rounded-xl border bg-transparent py-3 pl-11 pr-4 text-sm outline-none transition-all
                    ${
                      emailTouched && !isEmailValid
                        ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                        : emailTouched && isEmailValid
                          ? "border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                          : "border-stroke focus:border-primary focus:ring-2 focus:ring-primary/20"
                    }
                    dark:border-form-strokedark dark:bg-form-input
                  `}
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => setEmailTouched(true)}
                  onKeyPress={handleKeyPress}
                  aria-label="Email"
                  aria-invalid={emailTouched && !isEmailValid}
                />
              </div>
              {emailTouched && !isEmailValid && email && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  Email inválido
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block mb-2 text-sm font-medium text-black dark:text-white">
                Senha
              </label>
              <div className="relative">
                <FiLock
                  className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                    passwordTouched && !isPasswordValid
                      ? "text-red-400"
                      : passwordTouched && isPasswordValid
                        ? "text-green-500"
                        : "text-gray-400"
                  }`}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className={`w-full rounded-xl border bg-transparent py-3 pl-11 pr-11 text-sm outline-none transition-all
                    ${
                      passwordTouched && !isPasswordValid
                        ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                        : passwordTouched && isPasswordValid
                          ? "border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                          : "border-stroke focus:border-primary focus:ring-2 focus:ring-primary/20"
                    }
                    dark:border-form-strokedark dark:bg-form-input
                  `}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => setPasswordTouched(true)}
                  onKeyPress={handleKeyPress}
                  aria-label="Senha"
                  aria-invalid={passwordTouched && !isPasswordValid}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none focus:text-primary"
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
              {passwordTouched && !isPasswordValid && password && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  A senha deve ter pelo menos 6 caracteres
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm font-medium text-primary hover:text-primary/80 dark:text-primary dark:hover:text-primary/80 transition-colors"
              >
                Esqueci minha senha
              </button>
            </div>

            {/* Button */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white transition-all duration-200
                ${
                  canSubmit
                    ? "bg-primary hover:opacity-90 hover:shadow-lg active:scale-[0.98]"
                    : "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                }
              `}
              aria-label="Entrar"
            >
              {load ? (
                <>
                  <Load className="w-5 h-5 animate-spin" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        title="Recuperar Senha"
        className="!max-w-md"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-300 text-sm">
            Digite seu email abaixo para receber as instruções de recuperação de
            senha.
          </p>

          <InputDefault
            label="Email"
            placeholder="seu@email.com"
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
          />

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowForgotPassword(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleResetPassword}
              disabled={resetLoading || !resetEmail}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2
                ${
                  resetLoading || !resetEmail
                    ? "bg-primary/50 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90"
                }`}
            >
              {resetLoading ? (
                <>
                  <Load className="w-4 h-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar Email"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LoginPage;
